// Apolo IDE — Hermes run wiring (composition root for the execution path).
//
// This is the ONE place where the Hermes interfaces layer (components/) wires
// infrastructure adapters into the application use cases — allowed by the
// Dependency Rule precisely because composition roots live at the outermost
// layer. The UI calls launchRun/launchFanOut and FIRES-AND-FORGETS: the run's
// durable state lives server-side (PATCHes by the use case) and the live view
// streams through hermesRunStore — the launcher never waits for completion.
//
// Both functions RETURN the lifecycle promise so tests can await the full
// drive; the attached catch handler keeps fire-and-forget honest: it warns
// AND surfaces the failure to the user via the house toast ('apolo:toast'),
// so an ignored promise never produces an unhandled rejection NOR a silent
// failure. opts (signal/onStarted) pass through to the use case untouched.

function toastError(message) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent('apolo:toast', { detail: { kind: 'error', message } }),
  );
}

import { hermesApi } from '../../hermes/infrastructure/hermesApiClient.js';
import * as hermesRunStore from '../../hermes/infrastructure/hermesRunStore.js';
import { makeAppExecutionAdapter } from '../../hermes/infrastructure/appExecutionAdapter.js';
import { makeWorktreeAllocator } from '../../hermes/infrastructure/worktreeAllocator.js';
import { makeEnsureTeamAgent } from '../../hermes/infrastructure/teamAgent.js';
import { makeTeamChat } from '../../hermes/infrastructure/teamChat.js';
import { makeRunAgent } from '../../hermes/application/runAgent.js';
import { makeFanOutAndMerge } from '../../hermes/application/fanOutAndMerge.js';
import { makePlanTeam } from '../../hermes/application/planTeam.js';
import { makeSynthesizeTeam } from '../../hermes/application/synthesizeTeam.js';
import { makeRunTeamTurn } from '../../hermes/application/runTeamTurn.js';
import { squadCallLog } from '../../hermes/infrastructure/squadCallLogClient.js';
import { redactSecrets } from '../../lib/secretRedaction.js';
// A4 (E2) — async steer/report: feed live child events into the pure orchestrator + detect drift.
import { createOrchestrator, ingestChildEvent, detectDrift } from './asyncOrchestrator.js';
import { readHermesAsyncEnabled } from '../../lib/flags/sprintFlags.js';
import { findModel } from '../../lib/modelRegistry.js';
// E8-T02 — doutrina do operário de EQUIPE: núcleo da LEI (fonte única em shared/)
// injetado no runTeamTurn pelo composition root (Dependency Rule intacta).
import { SCAFFOLD_BLOCK } from '../../../../shared/constitution/core.mjs';

export const TEAM_WORKER_DOCTRINE = [
  '[Doutrina do operário — obrigatória]',
  SCAFFOLD_BLOCK,
  'Honestidade: só afirme o que você VERIFICOU nesta subtask; não invente números/arquivos/métricas; se editou código, RODE a verificação e mostre ✅/❌; ao citar código, use caminho:linha.',
].join('\n');

// E2-WS3-T01 — registry-backed leader-usage pricer injected into runTeamTurn so
// the PLAN + SYNTHESIS cost folds into team stats at the real model price. Lives
// at the composition root (a component, not the application layer) so the
// Dependency Rule stays intact. Honors an OpenRouter-reported usage.cost first.
function priceLeaderUsage(usage, modelId) {
  if (!usage || typeof usage !== 'object') return 0;
  const reported = Number(usage.cost ?? usage.total_cost);
  if (Number.isFinite(reported) && reported >= 0) return reported;
  const entry = findModel(modelId);
  if (!entry) return 0;
  const inT = Number(usage.input_tokens ?? usage.prompt_tokens ?? 0) || 0;
  const outT = Number(usage.output_tokens ?? usage.completion_tokens ?? 0) || 0;
  const inP = entry.pricing?.input ?? 0;
  const outP = entry.pricing?.output ?? 0;
  return (inT * inP + outT * outP) / 1_000_000;
}

// Auto-appended memory content (= a run's finalText) is DURABLE in
// hermes_agent_memory, recallable back into later prompts, AND exportable via
// memory.portable — so a secret echoed in finalText would persist verbatim. We
// redact it at the SAME seam the rest of the front uses (lib/secretRedaction),
// here at the composition root: redacting in runAgent.js would force a lib
// import into the application layer (the Hermes arch gate forbids it), so the
// injected appendMemory is wrapped to mask `content` before the wire. This is
// the same Gap C (T10) treatment patchRunState already gives result/error.
function redactingAppend(append) {
  return (agentId, mem) =>
    append(agentId, { ...mem, content: redactSecrets(String(mem?.content ?? '')) });
}

// Shared composition of a single-run RunAgent (recallMemory → the adapter
// prepends recalled memory to the run prompt; appendMemory → the use case
// records a run-summary on success, D18). Wiring the real hermesApi methods
// HERE (the composition root) keeps the adapter injectable and the application
// layer free of infrastructure imports. launchRun and buildHermesDriveRun both
// build the SAME RunAgent through this — no drift in the adapter/append wiring.
function makeWiredRunAgent() {
  const executor = makeAppExecutionAdapter({ recallMemory: hermesApi.recallMemory });
  return makeRunAgent({
    api: hermesApi,
    executor,
    store: hermesRunStore,
    clock: Date.now,
    appendMemory: redactingAppend(hermesApi.appendMemory),
  });
}

/** Start + drive a single run for `agent`. Fire-and-forget safe. */
export function launchRun(agent, task, opts = {}) {
  const { runAgent } = makeWiredRunAgent();
  const promise = runAgent(agent, task, opts);
  promise.catch((e) => {
    console.warn('[hermes] run failed:', e);
    toastError('Falha ao iniciar o run');
  });
  return promise;
}

/** Start + drive a fan-out (parent + N children + merge). Fire-and-forget safe. */
export function launchFanOut(agent, task, subtasks, opts = {}) {
  const executor = makeAppExecutionAdapter({ recallMemory: hermesApi.recallMemory });
  const allocator = makeWorktreeAllocator();
  const { fanOutAndMerge } = makeFanOutAndMerge({
    api: hermesApi,
    executor,
    store: hermesRunStore,
    clock: Date.now,
    allocator,
    appendMemory: redactingAppend(hermesApi.appendMemory),
  });
  const promise = fanOutAndMerge(agent, task, subtasks, opts);
  promise.catch((e) => {
    console.warn('[hermes] fan-out failed:', e);
    toastError('Falha ao iniciar o fan-out');
  });
  return promise;
}

/**
 * Team Mode (sprint subagents-team) — wire do pipeline de equipe pro Code chat.
 *
 * Composition do C2: ensureAgent + planner/sintetizador (teamChat sobre
 * resolveBrain) + fanOut REUSANDO o driver D14 com dois ganchos injetados:
 * a SÍNTESE entra como o `merge` do fan-out (vira o result durável do parent
 * em hermes_runs — RunBoard mostra a síntese) e cada transição de operário é
 * espelhada em onChildEvent pro chat. Workers NÃO auto-appendam memória (o
 * driveRun deles nasce sem appendMemory) — a memória da equipe é UMA: a
 * síntese, appendada pelo use case no líder (redigida aqui, mesma LEI do
 * launchRun/launchFanOut).
 *
 * launchTeamTurn NUNCA lança por LLM ruim (fallbacks no use case); só propaga
 * erro irrecuperável do ledger — e o caller (SessionWorkspace) o trata como
 * erro padrão do chat.
 */
/**
 * @param {{text?: string, root?: string, brain?: {kind?: string, model?: string, baseUrl?: string, adapterId?: string, provider?: string},
 * cap?: number, workerModel?: string, readOnly?: boolean, signal?: AbortSignal,
 * onEvent?: (event: any) => void, callLog?: typeof squadCallLog}} [options]
 */
export function launchTeamTurn({
  text,
  root,
  brain,
  cap,
  workerModel,
  readOnly = false,
  signal,
  onEvent,
  callLog = squadCallLog,
} = {}) {
  // E2 — squad_call sink: o planner roda ANTES do run existir, então as
  // entradas 'reason'/'structure' são bufferizadas e dão flush quando o run id
  // chega (team_started, logo após startRun). 'worker'/'synthesis' chegam depois
  // e vão direto. O wrapper consome squad_call/team_started e repassa o resto.
  const sink = makeSquadCallSink(callLog);
  const wrappedOnEvent = (evt) => {
    if (sink.handle(evt)) return; // squad_call consumido — não vaza pro chat
    onEvent?.(evt);
  };

  const teamFanOut = ({ agent, task, subtasks, merge, onChildEvent: rawOnChildEvent, signal: s, onStarted }) => {
    // A4 (E2) — when ON, the pure orchestrator tracks each child's reports and flags drift
    // (stalled/failed) so the orchestrator can steer it without blocking the others. Flag OFF
    // (default) ⇒ onChildEvent is passed through untouched ⇒ byte-identical spawn-and-block.
    const asyncOn = readHermesAsyncEnabled();
    let orch = asyncOn ? createOrchestrator([]) : null; // children register by their real id via ingest
    const onChildEvent = !asyncOn ? rawOnChildEvent : (evt) => {
      rawOnChildEvent?.(evt);
      try {
        orch = ingestChildEvent(orch, { id: evt?.id ?? `child_${evt?.index ?? 0}`, state: evt?.state }, Date.now());
        const drift = detectDrift(orch, Date.now());
        for (const d of drift) onEvent?.({ type: 'hermes_drift', id: d.id, reason: d.reason });
      } catch { /* observability is best-effort; never break the fan-out */ }
    };
    // E1 — read-only baked into the WORKER executor: when the team's analysis
    // mode is on, every operário runs without write/exec tools. The lead's
    // plan/synthesis (teamChat, toolless) is unaffected.
    const executor = makeAppExecutionAdapter({ recallMemory: hermesApi.recallMemory, readOnly });
    // Workers sem auto-append (appendMemory default = no-op): 4 operários
    // appendando finalText viraria ruído na memória do líder (D5).
    const baseDrive = makeRunAgent({
      api: hermesApi,
      executor,
      store: hermesRunStore,
      clock: Date.now,
    }).driveRun;
    const drive = async (child, agentRec, opts) => {
      onChildEvent?.({ id: child.id, index: child.childIndex ?? null, state: 'running' });
      try {
        const out = await baseDrive(child, agentRec, opts);
        const state = out?.run?.state || (out?.result?.ok ? 'done' : 'failed');
        onChildEvent?.({ id: child.id, index: child.childIndex ?? null, state });
        return out;
      } catch (e) {
        onChildEvent?.({ id: child.id, index: child.childIndex ?? null, state: 'failed' });
        throw e;
      }
    };
    const { fanOutAndMerge } = makeFanOutAndMerge({
      api: hermesApi,
      executor,
      store: hermesRunStore,
      clock: Date.now,
      allocator: makeWorktreeAllocator(),
      driveRun: drive,
      merge,
    });
    return fanOutAndMerge(agent, task, subtasks, { signal: s, onStarted });
  };

  const chat = makeTeamChat();
  const runTeamTurn = makeRunTeamTurn({
    ensureAgent: makeEnsureTeamAgent({ api: hermesApi }),
    planner: makePlanTeam({ chat }),
    synthesizer: makeSynthesizeTeam({ chat }),
    fanOut: teamFanOut,
    recall: hermesApi.recallMemory,
    appendMemory: redactingAppend(hermesApi.appendMemory),
    clock: Date.now,
    priceUsage: priceLeaderUsage, // E2-WS3-T01 — real registry pricing for leader cost
    workerDoctrine: TEAM_WORKER_DOCTRINE, // E8-T02 — equipe deixa de rodar "sem os livros"
  });
  // .finally garante o drop do buffer órfão em QUALQUER saída sem run (solo,
  // abort, throw) — o turno encerra e nada fica pendurado.
  return runTeamTurn({ text, root, brain, cap, workerModel, signal, onEvent: wrappedOnEvent }).finally(() =>
    sink.drop(),
  );
}

/**
 * E2 — sink de squad_call (extraído pra ser testável puro). Bufferiza as
 * chamadas LLM do orquestrador emitidas ANTES do run existir (reason/structure)
 * e dá flush quando `team_started` traz o runId; depois disso, append direto.
 * `drop()` descarta o buffer quando NENHUM run foi criado (solo/abort/throw) —
 * sem team run não há onde ancorar (run_id é NOT NULL): descarte INTENCIONAL,
 * não perda silenciosa pendurada. `handle(evt)` retorna true se consumiu o
 * evento (squad_call), false caso contrário (o caller repassa pro chat).
 */
export function makeSquadCallSink(callLog) {
  const buffer = [];
  let boundRunId = null;
  return {
    handle(evt) {
      if (evt?.type === 'squad_call') {
        if (boundRunId) void callLog.append(boundRunId, evt.entry);
        else buffer.push(evt.entry);
        return true;
      }
      if (evt?.type === 'team_started' && evt.runId && !boundRunId) {
        boundRunId = evt.runId;
        for (const entry of buffer.splice(0)) void callLog.append(boundRunId, entry);
      }
      return false;
    },
    drop() {
      if (!boundRunId) buffer.length = 0;
    },
    get boundRunId() {
      return boundRunId;
    },
    get pending() {
      return buffer.length;
    },
  };
}

/**
 * Build the wired `driveRun(run, agent, { signal })` for the Mac runner's
 * scheduled-dispatch path (P3-T03b, D19). A Hermes schedule that fires while
 * the Mac is online enqueues a remote_dispatch with context.source ===
 * 'hermes_run'; useRemoteRunner claims it and drives the ALREADY-CREATED queued
 * run through this — the EXACT same RunAgent launchRun uses (tools + memory +
 * worktree root from the run record), so the lifecycle PATCHes flow to
 * hermes_runs identically. Reuses makeWiredRunAgent so the adapter/append wiring
 * never drifts from the interactive launcher.
 */
export function buildHermesDriveRun() {
  return makeWiredRunAgent().driveRun;
}
